import { Badge, Card, Clickable } from '@warp-ds/react';
import cn from 'classnames';
import { type MouseEvent, useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { trackFusClick } from '@/api/api';
import { FavoriteButton } from '@/components/favoriteButton/FavoriteButton';
import type { Filters, ResultItem as ResultItemType } from '@/domain';
import { trackClick } from '@/lib/tracking/tracking';
import { resultItemLocationString, resultItemPriceString } from '@/utils/formatting';
import styles from './ResultItem.module.css';

export type ResultItemProps = {
    item: ResultItemType;
    rank: number;
    loginId?: number;
    filters: Filters;
    imagePreloaded?: boolean;
};

const makeSrcSet = (path: string | undefined) =>
    path?.replace ? `${path.replace('/default/', '/640x480c/')} 640w, ` + `${path.replace('/default/', '/320x240c/')} 320w` : path;
const applyImageRule = (url: string | undefined, rule: string) => (url?.replace ? url.replace('/default/', rule) : url);

export const ResultItem = ({ filters, item, rank, imagePreloaded }: ResultItemProps) => {
    const { contextPath, apiExternalUrl } = useConfig();
    const [showModal, setShowModal] = useState(false);
    const searchDates = filters?.booking_from && filters?.booking_to;
    const searchNumGuests = filters?.no_of_beds_from && filters?.no_of_beds_from > 0;
    const isTransactionalAd = item.ad_type === 'private' && item.country === 'Norge';

    const handleFavouriteClick = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        setShowModal(true);
    };

    const createItemUrl = () => {
        const baseItemUrl = `${contextPath}/ad.html?finnkode=${item.id}`;
        const dateParams = searchDates ? `&fra=${filters?.booking_from}&til=${filters?.booking_to}` : '';
        const numBedsParam = searchNumGuests ? `&gjester=${filters?.no_of_beds_from}` : '';
        return baseItemUrl + dateParams + numBedsParam;
    };

    const selectItem = () => {
        // A click on a FUS ad must be tracked
        if (item.upsell) {
            trackFusClick(apiExternalUrl, item.id)
                .then((result) => console.log(result)) // TODO remove this logging
                .catch((reason) => console.error(`FUS click tracking failed: ${reason}`));
        }

        // Only do this when running in Cypress, i.e. this code will be removed from the production build
        if (process.env.NODE_ENV === 'development') {
            // To help out with verifying that a network call for tracking FUS is not done on
            // a non-FUS ad, set `__E2E_TEST.fusClicked` true/false on the `window` object
            // so it can be checked in the Cypress test
            // @ts-ignore __E2E_TEST does not exist on window
            const E2E_TEST = window.__E2E_TEST || {};
            E2E_TEST.fusClicked = !!item.upsell;
            // @ts-ignore __E2E_TEST does not exist on window
            window.__E2E_TEST = E2E_TEST;
        }

        trackClick({
            type: 'Click',
            intent: 'Open',
            name: 'Search result clicked',
            object: {
                id: item.id,
                type: 'ListingItem',
                name: 'Search result',
                rank: rank + 1, // index is zero-based
                isTopPremiumPosition: item.upsell === true,
                paidPromotionType: item.upsell === true ? 'fus' : undefined,
                paidPromotions: item.fus_classified_product_id
                    ? [
                          {
                              '@id': `urn:product:product-instance:${item.fus_classified_product_id}`,
                          },
                      ]
                    : undefined,
            },
            target: {
                id: item.id,
                type: 'ClassifiedAd',
                name: item.heading,
                contentId: item.id,
                category: 'travel > holiday rental',
            },
            vertical: {
                name: 'travel',
                subVertical: 'holiday_rental',
            },
        });
    };

    const showTransactionBadge = () =>
        isTransactionalAd ? (
            <Badge variant="info" position="top-left" className="px-8 gap-4">
                Enkel bestilling
            </Badge>
        ) : (
            // biome-ignore lint/complexity/noUselessFragments: intended
            <></>
        );

    const showUpsellAds = () =>
        item.upsell ? (
            <Badge variant="sponsored" className="p-4 mb-12">
                Betalt plassering
            </Badge>
        ) : null;

    const itemUrl = createItemUrl();

    return (
        <Card className={styles.resultItem} data-item-finnid={item.id} data-testid={`resultItem-${item.id}`}>
            <div aria-owns={`adTitle${rank}`} />
            <img
                className="h-144 w-full object-cover"
                src={applyImageRule(item.imageurl, '/480w/')}
                srcSet={makeSrcSet(item.imageurl)}
                alt="Annonsebilde"
                aria-hidden="true"
                data-testid="adImage"
                sizes="(min-width: 990px) 300px, (min-width: 768px) 33vw, (min-width: 480px) 50vw"
                loading={imagePreloaded ? undefined : 'lazy'}
            />
            {showTransactionBadge()}
            <div className="p-16 relative">
                {showUpsellAds()}
                <p className="text-12 text-gray-500 truncate pr-32">{resultItemLocationString(item)}</p>
                <h3 className="truncate text-16 font-normal text-gray-800" data-testid="adTitle" id={`adTitle${rank}`}>
                    <Clickable
                        href={itemUrl}
                        target="_blank"
                        title={item.heading}
                        className="text-current hover:no-underline focus:no-underline"
                        onClick={selectItem}
                        data-testid={item.upsell && 'upsellClickable'}>
                        {item.heading}
                    </Clickable>
                </h3>
                {/* biome-ignore lint/a11y/noStaticElementInteractions: intended */}
                <div className={cn(styles.favoriteIcon, 'absolute')} data-testid="favoriteIcon" onClick={handleFavouriteClick}>
                    <FavoriteButton itemId={Number(item.id)} heartVariant="heart-on-image" showModal={showModal} />
                </div>
                {resultItemPriceString(item)}
                <p className="text-14 text-gray-500 mb-0" data-testid="bedroomCapacity">
                    {item.no_of_bedrooms} soverom <span className="text-gray-500">•</span> {item.sleeping_capacity} sengeplasser
                </p>
                <p className="text-14 text-gray-500 mt-8 mb-0">{item.ad_type !== 'private' ? 'Bedrift' : 'Privat'}</p>
            </div>
        </Card>
    );
};
